import { DomainEvent, DomainEventPublisher } from '../../domain/events/domainEvents';

/**
 * In-memory domain event publisher.
 *
 * The persistent `domainEvents` storage log this class used to keep (read →
 * append → write → verification read on every publish, capped at 1000 events)
 * is gone: nothing ever consumed it, it retained deleted-prompt metadata, it
 * bypassed the storage singleton and the repository lock, and — worst — a
 * failed log write made `publish` throw, so a prompt/folder deletion that had
 * already succeeded was reported to the user as failed.
 *
 * `publish` now only fans out to in-memory subscribers and NEVER throws:
 * event delivery is best-effort and must not fail the parent operation.
 */
export class ChromeEventPublisher implements DomainEventPublisher {
  private handlers = new Map<string, Array<(event: DomainEvent) => Promise<void>>>();

  async publish(event: DomainEvent): Promise<void> {
    const handlers = this.handlers.get(event.type) || [];
    const handlerResults = await Promise.allSettled(handlers.map(handler => handler(event)));

    handlerResults.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.error(`Event handler ${index} failed for ${event.type}:`, result.reason);
      }
    });
  }

  subscribe<T extends DomainEvent['type']>(
    eventType: T,
    handler: (event: Extract<DomainEvent, { type: T }>) => Promise<void>
  ): () => void {
    const handlers = this.handlers.get(eventType) || [];
    handlers.push(handler as (event: DomainEvent) => Promise<void>);
    this.handlers.set(eventType, handlers);

    return () => this.unsubscribe(eventType, handler as (event: DomainEvent) => Promise<void>);
  }

  private unsubscribe(eventType: string, handler: (event: DomainEvent) => Promise<void>): void {
    const handlers = this.handlers.get(eventType) || [];
    const index = handlers.indexOf(handler);
    if (index !== -1) {
      handlers.splice(index, 1);
      if (handlers.length === 0) {
        this.handlers.delete(eventType);
      } else {
        this.handlers.set(eventType, handlers);
      }
    }
  }
}
